import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import apiConfig from "../utils/apiConfig";
import {
  IAddTicketCommentRequest,
  ITicketAttachment,
  ICreateTicketRequest,
  IGetTicketResponse,
  IGetTicketsResponse,
  ITicket,
  ITicketNote,
  IUpdateTicketRequest,
} from "../models/Ticket";
import { DefaultResponseWithData } from "../models/system";

export default function useTickets(ticketId?: string) {
  const { get, post, put, postDocs } = useApiHelper(apiConfig("Tickets"));
  const queryClient = useQueryClient();

  const tickets = useQuery({
    queryKey: ["tickets"],
    queryFn: () => get<IGetTicketsResponse>("/ticket"),
    staleTime: 1000 * 60 * 2,
  });

  const ticket = useQuery({
    queryKey: ["ticket", ticketId],
    queryFn: () => get<IGetTicketResponse>(`/ticket/${ticketId}`),
    enabled: !!ticketId,
  });

  const createTicket = useMutation(
    (request: ICreateTicketRequest) =>
      post<ICreateTicketRequest, DefaultResponseWithData<ITicket>>("/ticket", request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["tickets"]);
      },
    }
  );

  const addComment = useMutation(
    ({
      ticketId,
      request,
    }: {
      ticketId: string;
      request: IAddTicketCommentRequest;
    }) =>
      post<IAddTicketCommentRequest, DefaultResponseWithData<ITicketNote>>(
        `/ticket/${ticketId}/notes`,
        request
      ),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["tickets"]);
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
    }
  );

  const updateTicket = useMutation(
    ({
      ticketId,
      request,
    }: {
      ticketId: string;
      request: IUpdateTicketRequest;
    }) =>
      put<IUpdateTicketRequest, DefaultResponseWithData<ITicket>>(
        `/ticket/${ticketId}`,
        request
      ),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["tickets"]);
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
    }
  );

  const uploadAttachment = useMutation(
    ({ ticketId, data }: { ticketId: string; data: FormData }) =>
      postDocs<DefaultResponseWithData<ITicketAttachment>>(
        `/ticket/${ticketId}/attachments`,
        data
      ),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["tickets"]);
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
    }
  );

  return {
    tickets,
    ticket,
    createTicket,
    updateTicket,
    addComment,
    uploadAttachment,
  };
}
